import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronLeft, Check, Home } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EstimateForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [settings, setSettings] = useState(null);
  
  // Protection contre les soumissions multiples
  const isSubmittingRef = useRef(false);
  const hasSubmittedRef = useRef(false);
  
  const [formData, setFormData] = useState({
    customer: {
      name: '',
      email: '',
      phone: '',
      address: ''
    },
    project: {
      project_type: '',
      model: '',
      width: 0,
      length: 0,
      surface: 0,
      options: [],
      start_date: new Date().toISOString().split('T')[0]
    }
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [segmentFound, setSegmentFound] = useState(null);
  const [checkingSegment, setCheckingSegment] = useState(false);
  
  // UTM Parameters pour attribution marketing
  const [utmParams, setUtmParams] = useState({
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    campaign_id: null
  });

  // Charger les UTM params depuis l'URL
  useEffect(() => {
    const utm_source = searchParams.get('utm_source');
    const utm_medium = searchParams.get('utm_medium');
    const utm_campaign = searchParams.get('utm_campaign');
    const campaign_id = searchParams.get('campaign_id');
    
    if (utm_source || utm_medium || utm_campaign || campaign_id) {
      setUtmParams({
        utm_source: utm_source || null,
        utm_medium: utm_medium || null,
        utm_campaign: utm_campaign || null,
        campaign_id: campaign_id || null
      });
      console.log('📊 UTM Parameters loaded:', { utm_source, utm_medium, utm_campaign, campaign_id });
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Check if segment exists when dimensions change
  useEffect(() => {
    const checkSegment = async () => {
      if (formData.project.width > 0 && formData.project.length > 0 && formData.project.project_type) {
        setCheckingSegment(true);
        try {
          await axios.post(`${API}/find-segment`, null, {
            params: {
              project_type: formData.project.project_type,
              largeur: formData.project.width,
              longueur: formData.project.length
            }
          });
          setSegmentFound(true);
        } catch (error) {
          setSegmentFound(false);
        } finally {
          setCheckingSegment(false);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      checkSegment();
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [formData.project.width, formData.project.length, formData.project.project_type]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleModelChange = (model) => {
    const dimensions = model.split('×');
    if (dimensions.length === 2) {
      const width = parseFloat(dimensions[0].match(/\d+/)[0]);
      const length = parseFloat(dimensions[1].match(/\d+/)[0]);
      const surface = width * length;
      
      updateFormData('project', 'model', model);
      updateFormData('project', 'width', width);
      updateFormData('project', 'length', length);
      updateFormData('project', 'surface', surface);
      updateFormData('project', 'total_surface', surface);
    }
  };

  const handleOptionToggle = (optionName) => {
    const currentOptions = formData.project.options;
    const newOptions = currentOptions.includes(optionName)
      ? currentOptions.filter(o => o !== optionName)
      : [...currentOptions, optionName];
    updateFormData('project', 'options', newOptions);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    updateFormData('project', 'start_date', date.toISOString().split('T')[0]);
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.customer.name || !formData.customer.email || !formData.customer.phone || !formData.customer.address) {
          toast.error('Veuillez remplir tous les champs');
          return false;
        }
        break;
      case 2:
        if (!formData.project.project_type) {
          toast.error('Veuillez choisir un type de projet');
          return false;
        }
        break;
      case 3:
        if (!formData.project.width || formData.project.width <= 0) {
          toast.error('Veuillez saisir une largeur valide');
          return false;
        }
        if (!formData.project.length || formData.project.length <= 0) {
          toast.error('Veuillez saisir une longueur valide');
          return false;
        }
        break;
      case 4:
        // Step 4 n'existe plus (anciennement Annexes)
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    // Protection contre les soumissions multiples
    if (isSubmittingRef.current || hasSubmittedRef.current) {
      console.log('⚠️ Soumission déjà en cours ou déjà effectuée');
      return;
    }
    
    isSubmittingRef.current = true;
    setLoading(true);
    
    try {
      // Find matching segment based on main project dimensions
      const segmentResponse = await axios.post(`${API}/find-segment`, null, {
        params: {
          project_type: formData.project.project_type,
          largeur: formData.project.width,
          longueur: formData.project.length
        }
      });
      
      // Calculate surface
      const surface = formData.project.width * formData.project.length;
      
      // Add segment info to formData + UTM parameters for marketing attribution
      const submissionData = {
        ...formData,
        project: {
          ...formData.project,
          model: segmentResponse.data.segment.label,
          surface: surface,
          price_per_sqft: segmentResponse.data.prix_par_pc
        },
        // Attribution marketing (UTM)
        ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
        ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
        ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
        ...(utmParams.campaign_id && { campaign_id: utmParams.campaign_id })
      };
      
      const response = await axios.post(`${API}/submissions`, submissionData);
      hasSubmittedRef.current = true;  // Marquer comme soumis avec succès
      toast.success('Soumission créée avec succès!');
      navigate(`/result/${response.data.id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        // Segment not found - create custom project request without pricing
        try {
          const surface = formData.project.width * formData.project.length;
          const customProjectData = {
            customer: formData.customer,
            project: {
              ...formData.project,
              model: `${formData.project.project_type} ${formData.project.width}' × ${formData.project.length}'`,
              surface: surface
            },
            // Attribution marketing (UTM) pour projets personnalisés aussi
            ...(utmParams.utm_source && { utm_source: utmParams.utm_source }),
            ...(utmParams.utm_medium && { utm_medium: utmParams.utm_medium }),
            ...(utmParams.utm_campaign && { utm_campaign: utmParams.utm_campaign }),
            ...(utmParams.campaign_id && { campaign_id: utmParams.campaign_id })
          };
          
          const customResponse = await axios.post(`${API}/custom-project-request`, customProjectData);
          hasSubmittedRef.current = true;  // Marquer comme soumis avec succès
          toast.success('Demande de projet personnalisé envoyée!', { duration: 5000 });
          navigate(`/custom-result/${customResponse.data.id}`);
        } catch (customError) {
          toast.error('Erreur lors de la création de la demande');
          console.error(customError);
          isSubmittingRef.current = false;  // Permettre de réessayer en cas d'erreur
        }
      } else {
        toast.error('Erreur lors de la création de la soumission');
        console.error(error);
        isSubmittingRef.current = false;  // Permettre de réessayer en cas d'erreur
      }
    } finally {
      setLoading(false);
    }
  };

  const getProjectTypeModels = () => {
    if (!settings) return [];
    if (formData.project.project_type === 'Maison de parc') {
      return settings.models.filter(m => m.name.includes('Maison de parc'));
    } else if (formData.project.project_type === 'UHA') {
      return settings.models.filter(m => m.name.includes('UHA'));
    }
    return [];
  };

  const renderStepIndicator = () => (
    <div className="mb-8 sm:mb-12">
      {/* Mobile: Simple progress bar */}
      <div className="block sm:hidden">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium" style={{ color: '#335C2E' }}>
            Étape {step} sur 6
          </span>
          <span className="text-xs" style={{ color: '#666666' }}>
            {Math.round((step / 6) * 100)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${(step / 6) * 100}%`,
              background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)'
            }}
          />
        </div>
      </div>

      {/* Desktop: Full step indicator */}
      <div className="hidden sm:flex items-center justify-center">
        {[1, 2, 3, 4, 5, 6].map((s) => (
          <div key={s} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                s === step
                  ? 'bg-gradient-to-br from-green-700 to-green-600 text-white scale-110'
                  : s < step
                  ? 'bg-gradient-to-br from-green-700 to-green-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
              style={s === step || s < step ? { background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)' } : {}}
            >
              {s < step ? <Check className="w-5 h-5" /> : s}
            </div>
            {s < 6 && (
              <div
                className={`w-12 h-1 mx-2 transition-all ${
                  s < step ? 'bg-green-700' : 'bg-gray-200'
                }`}
                style={s < step ? { background: '#335C2E' } : {}}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-6 sm:py-12 px-4" style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm mb-3 sm:mb-4 hover:underline"
            style={{ color: '#335C2E' }}
            data-testid="back-home-btn"
          >
            <Home className="w-4 h-4 mr-1" />
            Retour à l'accueil
          </button>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold px-2" style={{ color: '#335C2E' }}>
            Estimation de projet
          </h1>
        </div>

        <div className="card">
          {renderStepIndicator()}

          {/* Step 1: Customer Info */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-customer-info">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Vos coordonnées</h2>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Nom complet</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.customer.name}
                  onChange={(e) => updateFormData('customer', 'name', e.target.value)}
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Courriel</label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.customer.email}
                  onChange={(e) => updateFormData('customer', 'email', e.target.value)}
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Téléphone</label>
                <input
                  type="tel"
                  className="input-field"
                  value={formData.customer.phone}
                  onChange={(e) => updateFormData('customer', 'phone', e.target.value)}
                  data-testid="customer-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Adresse complète</label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={formData.customer.address}
                  onChange={(e) => updateFormData('customer', 'address', e.target.value)}
                  data-testid="customer-address-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Project Type */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-project-type">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Type de projet</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div
                  onClick={() => updateFormData('project', 'project_type', 'Maison de parc')}
                  className={`card cursor-pointer transition-all border-2 ${
                    formData.project.project_type === 'Maison de parc'
                      ? 'border-green-700 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={formData.project.project_type === 'Maison de parc' ? { borderColor: '#335C2E' } : {}}
                  data-testid="project-type-maison"
                >
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#C7A027' }}>Maison de parc</h3>
                  <p className="text-sm" style={{ color: '#666666' }}>Espace de vie confortable et élégant</p>
                </div>
                <div
                  onClick={() => updateFormData('project', 'project_type', 'UHA')}
                  className={`card cursor-pointer transition-all border-2 ${
                    formData.project.project_type === 'UHA'
                      ? 'border-green-700 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={formData.project.project_type === 'UHA' ? { borderColor: '#335C2E' } : {}}
                  data-testid="project-type-uha"
                >
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#C7A027' }}>UHA</h3>
                  <p className="text-sm" style={{ color: '#666666' }}>Unité Habitable Accessoire</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Dimensions */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-dimensions">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Dimensions du projet</h2>
              <p className="text-sm mb-6" style={{ color: '#666666' }}>
                Saisissez les dimensions de votre projet. Le prix sera calculé automatiquement selon le segment correspondant.
              </p>

              {/* Liste des segments disponibles */}
              {settings && formData.project.project_type && (
                <div className="p-4 rounded-lg mb-6" style={{ background: '#F0F7F4', border: '1px solid #C7A027' }}>
                  <h3 className="text-sm font-semibold mb-3" style={{ color: '#335C2E' }}>
                    📐 Dimensions disponibles pour {formData.project.project_type} :
                  </h3>
                  <div className="space-y-2">
                    {settings.segments
                      .filter(seg => seg.project_type === formData.project.project_type)
                      .sort((a, b) => {
                        if (a.largeur_ft !== b.largeur_ft) return a.largeur_ft - b.largeur_ft;
                        return a.longueur_min_ft - b.longueur_min_ft;
                      })
                      .map((segment, idx) => (
                        <div key={idx} className="flex items-center text-sm" style={{ color: '#555555' }}>
                          <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: '#C7A027' }}></span>
                          <span>
                            <strong>Largeur {segment.largeur_ft}'</strong> × Longueur <strong>{segment.longueur_min_ft}' - {segment.longueur_max_ft}'</strong>
                          </span>
                        </div>
                      ))}
                  </div>
                  <p className="text-xs mt-3 italic" style={{ color: '#666666' }}>
                    💡 Pour des dimensions hors de ces plages, un devis personnalisé sera établi.
                  </p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                    Largeur (pieds)
                  </label>
                  <input
                    type="number"
                    value={formData.project.width || ''}
                    onChange={(e) => updateFormData('project', 'width', parseFloat(e.target.value) || 0)}
                    placeholder="Ex: 10"
                    className="input-field w-full"
                    step="0.1"
                    data-testid="project-width-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                    Longueur (pieds)
                  </label>
                  <input
                    type="number"
                    value={formData.project.length || ''}
                    onChange={(e) => updateFormData('project', 'length', parseFloat(e.target.value) || 0)}
                    placeholder="Ex: 24"
                    className="input-field w-full"
                    step="0.1"
                    data-testid="project-length-input"
                  />
                </div>
              </div>

              {formData.project.width > 0 && formData.project.length > 0 && (
                <>
                  {checkingSegment ? (
                    <div className="p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                      <p className="text-sm" style={{ color: '#666666' }}>
                        Vérification du segment en cours...
                      </p>
                    </div>
                  ) : segmentFound === true ? (
                    <div className="p-4 rounded-lg" style={{ background: '#E8F5E9' }}>
                      <p className="text-sm font-medium" style={{ color: '#2E7D32' }}>
                        ✓ Surface calculée : <strong>{(formData.project.width * formData.project.length).toFixed(2)} pi²</strong>
                      </p>
                      <p className="text-xs mt-1" style={{ color: '#666666' }}>
                        Le prix sera déterminé automatiquement selon le segment approprié
                      </p>
                    </div>
                  ) : segmentFound === false ? (
                    <div className="p-4 rounded-lg border-2" style={{ background: '#FFF8E1', borderColor: '#F59E0B' }}>
                      <p className="text-sm font-medium mb-2" style={{ color: '#92400E' }}>
                        ℹ️ Projet personnalisé
                      </p>
                      <p className="text-sm" style={{ color: '#78350F' }}>
                        Les estimations pour les projets personnalisés se font sur rendez-vous dans nos locaux.
                      </p>
                      <p className="text-xs mt-2" style={{ color: '#92400E' }}>
                        Vous pouvez continuer le formulaire pour nous fournir plus de détails, ou nous contacter directement.
                      </p>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          )}

          {/* Step 4: Options */}
          {step === 4 && settings && (
            <div className="space-y-6" data-testid="step-options">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Options supplémentaires</h2>
              <p className="text-sm mb-4 sm:mb-6" style={{ color: '#666666' }}>Sélectionnez les options que vous souhaitez ajouter à votre projet</p>
              
              {/* Filtrer les options selon le type de projet */}
              {(() => {
                const availableOptions = settings.options.filter(opt => 
                  opt.project_types && opt.project_types.includes(formData.project.project_type)
                );
                
                return (
                  <>
                    {/* Note importante pour les options à prix zéro */}
                    {availableOptions.some(opt => opt.price === 0) && (
                      <div className="p-4 rounded-lg border-l-4" style={{ background: '#FFF8E1', borderColor: '#F59E0B' }}>
                        <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                          ℹ️ <strong>Note importante :</strong> Certaines options ont un coût qui sera déterminé après un rendez-vous avec nos équipes Refacing VR. Le montant total estimé pourrait donc être ajusté.
                        </p>
                      </div>
                    )}

                    {availableOptions.length === 0 ? (
                      <div className="text-center p-8" style={{ color: '#999999' }}>
                        <p>Aucune option disponible pour ce type de projet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {availableOptions.map((option) => (
                  <div
                    key={option.name}
                    onClick={() => handleOptionToggle(option.name)}
                    className={`card cursor-pointer transition-all border-2 ${
                      formData.project.options.includes(option.name)
                        ? 'border-green-700 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={formData.project.options.includes(option.name) ? { borderColor: '#335C2E' } : {}}
                    data-testid={`option-${option.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold" style={{ color: '#333333' }}>{option.name}</h3>
                        {option.price > 0 ? (
                          <p className="text-sm mt-1" style={{ color: '#C7A027' }}>{option.price.toLocaleString()} $</p>
                        ) : (
                          <p className="text-sm mt-1" style={{ color: '#F59E0B' }}>Coût à déterminer après rendez-vous</p>
                        )}
                      </div>
                      <div
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          formData.project.options.includes(option.name) ? 'bg-green-700 border-green-700' : 'border-gray-300'
                        }`}
                        style={formData.project.options.includes(option.name) ? { background: '#335C2E', borderColor: '#335C2E' } : {}}
                      >
                        {formData.project.options.includes(option.name) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Step 5: Start Date */}
          {step === 5 && (
            <div className="space-y-6" data-testid="step-start-date">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Date de démarrage</h2>
              <p className="text-sm mb-4 sm:mb-6" style={{ color: '#666666' }}>Sélectionnez la date prévue pour le début de votre projet</p>
              <div className="flex justify-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="btn-primary" data-testid="select-date-btn">
                      {format(selectedDate, 'PPP', { locale: fr })}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date()}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="mt-8 p-6 rounded-lg" style={{ background: 'linear-gradient(135deg, #F5F5F5 0%, #E8E4D9 100%)' }}>
                <p className="text-sm mb-2" style={{ color: '#666666' }}>Date sélectionnée:</p>
                <p className="text-xl font-bold" style={{ color: '#335C2E' }}>
                  {format(selectedDate, 'PPPP', { locale: fr })}
                </p>
              </div>
            </div>
          )}

          {/* Step 6: Summary */}
          {step === 6 && (
            <div className="space-y-6 sm:space-y-8" data-testid="step-summary">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6" style={{ color: '#333333' }}>Récapitulatif</h2>
              
              <div>
                <h3 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#C7A027' }}>Client</h3>
                <div className="space-y-1 text-sm" style={{ color: '#666666' }}>
                  <p><strong>Nom:</strong> {formData.customer.name}</p>
                  <p><strong>Courriel:</strong> {formData.customer.email}</p>
                  <p><strong>Téléphone:</strong> {formData.customer.phone}</p>
                  <p><strong>Adresse:</strong> {formData.customer.address}</p>
                </div>
              </div>

              <div className="section-divider" />

              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: '#C7A027' }}>Projet</h3>
                <div className="space-y-1 text-sm" style={{ color: '#666666' }}>
                  <p><strong>Type:</strong> {formData.project.project_type}</p>
                  <p><strong>Modèle:</strong> {formData.project.model}</p>
                  <p><strong>Dimensions:</strong> {formData.project.width} × {formData.project.length} pi</p>
                  <p><strong>Surface:</strong> {formData.project.surface} pi²</p>
                  <p><strong>Options:</strong> {formData.project.options.length > 0 ? formData.project.options.join(', ') : 'Aucune'}</p>
                  <p><strong>Date de démarrage:</strong> {format(selectedDate, 'PPP', { locale: fr })}</p>
                </div>
              </div>

              {/* Note si options à coût zéro sélectionnées */}
              {settings && formData.project.options.length > 0 && 
               settings.options.filter(opt => opt.price === 0 && formData.project.options.includes(opt.name)).length > 0 && (
                <div className="p-4 rounded-lg border-l-4" style={{ background: '#FFF8E1', borderColor: '#F59E0B' }}>
                  <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                    ⚠️ <strong>Attention :</strong> Certaines options sélectionnées ont un coût qui sera déterminé après rendez-vous. Le montant total pourrait être ajusté.
                  </p>
                </div>
              )}

              <div className="mt-8 p-6 rounded-lg text-center" style={{ background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)', color: 'white' }}>
                <p className="text-sm mb-2">Prêt à soumettre votre demande?</p>
                <p className="text-lg font-semibold">Vous recevrez votre estimation détaillée instantanément</p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 sm:mt-12 gap-3">
            {step > 1 && (
              <button
                onClick={prevStep}
                className="btn-secondary text-sm sm:text-base px-4 sm:px-6"
                data-testid="prev-step-btn"
              >
                <ChevronLeft className="inline-block mr-1 w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Précédent</span>
                <span className="inline sm:hidden">Préc.</span>
              </button>
            )}
            {step < 6 ? (
              <button
                onClick={nextStep}
                className="btn-primary ml-auto text-sm sm:text-base px-4 sm:px-6"
                data-testid="next-step-btn"
              >
                Suivant
                <ChevronRight className="inline-block ml-1 w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary ml-auto text-sm sm:text-base px-4 sm:px-6"
                data-testid="submit-estimate-btn"
              >
                {loading ? 'Envoi...' : 'Soumettre'}
                <Check className="inline-block ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimateForm;
